import { http } from '@/lib/http';
import type {
  Contract,
  ContractStatus,
  CreateContractInput,
  ExtractionResult,
  UpdateContractInput,
} from './types';

export interface ContractFilters {
  airport?: string;
  status?: ContractStatus;
  expiringWithinDays?: number;
}

export async function listContracts(filters: ContractFilters = {}): Promise<Contract[]> {
  const search = new URLSearchParams();
  if (filters.airport) search.set('airport', filters.airport);
  if (filters.status) search.set('status', filters.status);
  if (filters.expiringWithinDays)
    search.set('expiringWithinDays', String(filters.expiringWithinDays));
  const qs = search.toString();
  return http.get(`v1/contracts${qs ? `?${qs}` : ''}`).json<Contract[]>();
}

export async function getContract(contractUrn: string): Promise<Contract> {
  return http.get(`v1/contracts/${encodeURIComponent(contractUrn)}`).json<Contract>();
}

export async function createContract(input: CreateContractInput): Promise<Contract> {
  return http.post('v1/contracts', { json: input }).json<Contract>();
}

export async function updateContract(
  contractUrn: string,
  input: UpdateContractInput,
): Promise<Contract> {
  return http
    .put(`v1/contracts/${encodeURIComponent(contractUrn)}`, { json: input })
    .json<Contract>();
}

export async function archiveContract(contractUrn: string): Promise<Contract> {
  return http
    .post(`v1/contracts/${encodeURIComponent(contractUrn)}/archive`)
    .json<Contract>();
}

/** AI wizard: extract structured terms from an uploaded contract PDF. */
/**
 * Send the PDF to the BFF's contract reader.
 *
 * Returns `{ draft, provenance }` rather than a bare contract: the draft is
 * only half the answer, and `provenance` carries which model read it (or
 * `'mock'` when none is configured) plus the fields the document never stated.
 */
export async function extractContractFromPdf(file: File): Promise<ExtractionResult> {
  const body = new FormData();
  body.append('file', file);
  // No explicit timeout override: reading a scanned rate sheet is a model call,
  // and the BFF already caps it at CONTRACTS_EXTRACTION_TIMEOUT_MS.
  return http.post('v1/contracts/from-pdf', { body, timeout: false }).json<ExtractionResult>();
}
